import { useState } from "react";
import { Copy, Check, Download } from "lucide-react";
import { toast } from "sonner";
import { Reveal, Overline } from "../components/Reveal";
import Seo from "../components/Seo";

const COLOURS = [
  { name: "Obsidian Black", hex: "#050505", usage: "Backgrounds, base" },
  { name: "Neon Gold", hex: "#FFD84A", usage: "Primary accent, CTAs" },
  { name: "White Gold", hex: "#FFE98A", usage: "Hover states, highlights" },
  { name: "Metallic Gold", hex: "#D4AF37", usage: "Secondary accent, headings" },
  { name: "Rose Glow", hex: "#FF6E8C", usage: "Glow tinge only" },
  { name: "Pure White", hex: "#FFFFFF", usage: "Body text" },
];

const PHOTOS = [
  { url: "/brand/speedy-resurfacing-logo.png", caption: "Official Speedy Resurfacing logo — use on every post, story and print item. Never stretch, recolour or crop the gold frame." },
  { url: "/api/files/speedyz/uploads/98acd981-5c8b-4b14-821a-2704a4316364.jpg", caption: "Two royal blue tennis courts, resurfaced and line marked in Sydney. #tenniscourtresurfacing #speedyresurfacing" },
  { url: "/api/files/speedyz/uploads/4b5108b7-d3b7-413e-89c1-39d6a980dd9c.jpg", caption: "Courtside on a fresh royal blue acrylic tennis court. Hand-masked lines, tournament dimensions. #linemarking #sydney" },
  { url: "/api/files/speedyz/uploads/0a2dfa8a-0f08-4fe1-a223-71ee3bd327c1.webp", caption: "Backyard goals unlocked. Sky blue + charcoal Jumpman key. Sydney backyard court by Team Speedy. #courtresurfacing #sydneybuilds" },
  { url: "/api/files/speedyz/uploads/15930ed0-48ec-4f38-99dd-924ada4b6717.jpg", caption: "From spare corner to family half-court. Royal blue on slate grey. #backyardcourt #speedyresurfacing" },
  { url: "/api/files/speedyz/uploads/fefb81f1-2bcd-47e8-a58f-0d4cde62ce01.jpg", caption: "Indoor. Timber. That centre logo. A premium indoor performance court. #indoorcourt #courtdesign" },
  { url: "/api/files/speedyz/uploads/f3a0755d-cd79-4350-8a68-0698175b22fd.webp", caption: "Teal x sandstone x orange key. Custom colours, zero compromise. #courtdesign #linemarking" },
  { url: "/api/files/speedyz/uploads/9f55a100-4119-4504-9519-1e8320c1a6aa.webp", caption: "Golden hour on a fresh surface. Every line laser-straight. #courtresurfacing" },
  { url: "/api/files/speedyz/uploads/8056e79e-6287-4cdc-a166-baa8b679b5fb.webp", caption: "School courts done right — colour-coded, multi-sport, built to last. #schoolcourts #nsw" },
  { url: "/api/files/speedyz/uploads/1fd3919a-e932-45f6-80fe-f1e9f663456a.webp", caption: "Big red. School centre court with blue overlays. #courtpainting #sydney" },
  { url: "/api/files/speedyz/uploads/3a705fec-4364-4f20-b71d-4f6bb344a7c9.webp", caption: "Nice one 👌 Multi-court precinct, competition-ready. #netball #resurfacing" },
];

const BRAIN_TEXT = `BRAND: Speedy Resurfacing (division of Speedy Line Marking, Sydney)
WEBSITE: https://www.speedyresurfacing.com.au
LINK-IN-BIO: https://www.speedyresurfacing.com.au/links
WHAT WE DO: Court resurfacing, custom court design, precision line marking — basketball, tennis, netball, school & commercial courts across Sydney and NSW (Sydney CBD, Sutherland Shire, Liverpool, Wollongong, Newcastle, Canberra + more).
TONE OF VOICE: Confident, punchy, premium. Short sentences. Trade pride. Never corporate. Occasional all-caps for impact ("REAL COURTS. REAL WORK."). Australian spelling.
TAGLINES: "Your Court. Rebuilt." / "Speed Force — Resurfacing" / "One Team. One Standard."
BRAND COLOURS: Obsidian Black #050505, Neon Gold #FFD84A, Metallic Gold #D4AF37, White #FFFFFF (never use green).
CTA EVERY POST: Free quote at www.speedyresurfacing.com.au/quote or upload a court photo at www.speedyresurfacing.com.au/court-scan
PROOF POINTS: CM3 certified, trusted by schools & councils, 5-star Google reviews, 24/7 service.
HASHTAGS: #courtresurfacing #courtdesign #linemarking #sydneytrades #basketballcourt #netballcourt #schoolcourts #speedyresurfacing
CONTENT PILLARS: 1) Before/after transformations 2) Satisfying line-marking process clips 3) Custom colour showcases 4) Backyard court inspo 5) School/community projects.`;

const CopyBtn = ({ text, label = "Copy" }) => {
  const [done, setDone] = useState(false);
  return (
    <button
      onClick={() => { navigator.clipboard.writeText(text); setDone(true); toast.success("Copied"); setTimeout(() => setDone(false), 1500); }}
      className="inline-flex items-center gap-1.5 rounded-sm border border-neon/40 px-3 py-1.5 text-xs font-bold text-neon hover:bg-neon hover:text-obsidian transition-colors"
      data-testid="brand-copy-btn">
      {done ? <Check size={13} /> : <Copy size={13} />} {label}
    </button>
  );
};

export default function BrandKit() {
  return (
    <div className="pt-28 pb-24 min-h-screen noise" data-testid="brandkit-page">
      <Seo title="Brand Kit | Speedy Resurfacing" description="Official Speedy Resurfacing brand assets — colours, voice, photos and captions." path="/brand-kit" />
      <div className="mx-auto max-w-6xl px-5 md:px-10">
        <Reveal>
          <Overline>Internal · For Sintra & Social Tools</Overline>
          <h1 className="mt-5 font-heading font-black text-4xl md:text-6xl tracking-tighter">BRAND <span className="text-neon">KIT.</span></h1>
          <p className="mt-4 max-w-xl text-zinc-400">Everything your AI social team needs. Paste the Brain block into Sintra, grab photos + ready-made captions below.</p>
        </Reveal>

        {/* Sintra Brain block */}
        <Reveal delay={0.05}>
          <div className="mt-12 rounded-lg border border-gold/30 bg-obsidian-800 p-6 md:p-8">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <h2 className="font-heading font-black text-xl">PASTE THIS INTO SINTRA'S BRAIN</h2>
              <CopyBtn text={BRAIN_TEXT} label="Copy Brain Block" />
            </div>
            <pre className="mt-4 whitespace-pre-wrap font-mono text-xs leading-relaxed text-zinc-400 bg-obsidian-700 rounded-sm p-4 max-h-72 overflow-y-auto" data-testid="brain-text">{BRAIN_TEXT}</pre>
          </div>
        </Reveal>

        {/* Colours */}
        <Reveal delay={0.05}>
          <div className="mt-14">
            <h2 className="font-heading font-black text-2xl">BRAND COLOURS</h2>
            <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-4">
              {COLOURS.map((c) => (
                <div key={c.hex} className="glass rounded-lg p-4 flex items-center gap-4">
                  <span className="h-12 w-12 rounded-sm border border-white/10 shrink-0" style={{ backgroundColor: c.hex }} />
                  <div className="min-w-0">
                    <p className="font-bold text-sm">{c.name}</p>
                    <p className="font-mono text-xs text-zinc-500">{c.hex}</p>
                    <p className="text-[11px] text-zinc-500 truncate">{c.usage}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Reveal>

        {/* Photo library with captions */}
        <Reveal delay={0.05}>
          <div className="mt-14">
            <h2 className="font-heading font-black text-2xl">REAL PHOTO LIBRARY <span className="text-gold">+ READY CAPTIONS</span></h2>
            <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {PHOTOS.map((p, i) => (
                <div key={p.url} className="glass rounded-lg overflow-hidden flex flex-col" data-testid={`brand-photo-${i}`}>
                  <img src={p.url} alt={p.caption} loading="lazy" className="h-44 w-full object-cover" />
                  <div className="p-4 flex flex-col gap-3 flex-1">
                    <p className="text-xs text-zinc-400 flex-1">{p.caption}</p>
                    <div className="flex gap-2">
                      <CopyBtn text={p.caption} label="Caption" />
                      <a href={p.url} download target="_blank" rel="noreferrer"
                         className="inline-flex items-center gap-1.5 rounded-sm border border-white/15 px-3 py-1.5 text-xs font-bold text-zinc-300 hover:border-gold hover:text-gold transition-colors">
                        <Download size={13} /> Photo
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  );
}
